import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CONVERSION_SIGNALS, ParcelSearchCriteria, SearchBasis, SearchSort } from './ParcelSearchAPI';
import { option, optionSelected, sectionHint } from './optionStyles';

/**
 * Kriterien der Grundstückssuche. Oben die Suchen für die häufigen Fragen (Reserve, Baulücke, Aufstockung, PV-Dach); erst mit einer gewählten Suche erscheinen ihre Kriterien. Gesucht wird im Gebiet der Heatmap.
 */

type Preset = 'reserve' | 'vacant' | 'storeys' | 'pv' | 'conversion';

const PRESETS: Record<Preset, ParcelSearchCriteria> = {
    reserve: { minReserveM2: 500, sort: 'reserve' },
    vacant: { vacant: true, minAreaM2: 200, sort: 'area' },
    storeys: { minAddStoreys: 1, sort: 'storeys' },
    pv: { minPvYieldKWh: 20000, sort: 'pv' },
    conversion: { conversion: true, minAreaM2: 500, sort: 'area' },
};

// Welche Kriterien zu welcher Suche gehören.
type Field = 'reserve' | 'area' | 'storeys' | 'pv' | 'brw' | 'basis' | 'uses' | 'signals';
const FIELDS: Record<Preset, Field[]> = {
    reserve: ['reserve', 'area', 'brw', 'basis', 'uses'],
    vacant: ['area', 'brw', 'basis', 'uses'],
    storeys: ['storeys', 'brw', 'basis', 'uses'],
    pv: ['pv', 'area', 'uses'],
    conversion: ['signals', 'area', 'brw', 'uses'],
};

const USES = ['WA', 'WR', 'MI', 'MU', 'MK', 'GE', 'GI'];
const BASES: SearchBasis[] = ['BP', '34', '35'];

/**
 * Überschrift einer Knopfgruppe mit „i“ zum Antippen: die Liste der Kürzel mit ihrer Bedeutung. Der Mouse-over der
 * Knöpfe reicht am Rechner, auf dem iPad gibt es keinen.
 */
const HeadingWithLegend = ({ label, entries, infoLabel }: { label: string; entries: Array<[string, string]>; infoLabel: string }) => (
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        <Popover>
            <PopoverTrigger asChild>
                <button type="button" aria-label={infoLabel} className="rounded-full p-0.5 hover:bg-accent hover:text-foreground">
                    <Info className="h-3.5 w-3.5" aria-hidden />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 text-xs">
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
                    {entries.map(([key, text]) => (
                        <div key={key} className="contents">
                            <dt className="font-semibold">{key}</dt>
                            <dd className="text-muted-foreground">{text}</dd>
                        </div>
                    ))}
                </dl>
            </PopoverContent>
        </Popover>
    </span>
);

const numberOrUndefined = (value: string) => (value.trim() === '' || !Number.isFinite(Number(value)) ? undefined : Number(value));

interface Properties {
    hasArea: boolean;
    searching: boolean;
    onSearch: (criteria: ParcelSearchCriteria) => void;
}

export default function SearchSection({ hasArea, searching, onSearch }: Properties) {
    const { t } = useTranslation();
    const [preset, setPreset] = useState<Preset | null>(null);
    const [criteria, setCriteria] = useState<ParcelSearchCriteria>({});
    // Ein geändertes Kriterium bleibt in der gewählten Suche — die Kriterien gehören zu ihr.
    const set = (patch: Partial<ParcelSearchCriteria>) => setCriteria((current) => ({ ...current, ...patch }));
    const toggle = <T,>(list: T[] | undefined, value: T) =>
        list?.includes(value) ? list.filter((v) => v !== value) : [...(list ?? []), value];

    const shows = (key: Field) => preset !== null && FIELDS[preset].includes(key);

    const field = (id: keyof ParcelSearchCriteria, label: string, unit: string) => (
        <div className="flex flex-col gap-1">
            <Label htmlFor={`search-${id}`} className="text-xs text-muted-foreground">
                {label}
            </Label>
            <div className="flex items-center gap-1.5">
                <Input
                    id={`search-${id}`}
                    inputMode="numeric"
                    className="h-8"
                    value={(criteria[id] as number | undefined) ?? ''}
                    onChange={(event) => set({ [id]: numberOrUndefined(event.target.value) } as any)}
                />
                <span className="w-12 shrink-0 text-xs text-muted-foreground">{unit}</span>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-0.5">
                {(Object.keys(PRESETS) as Preset[]).map((key) => (
                    <button
                        key={key}
                        type="button"
                        className={cn(option, 'text-sm', preset === key && optionSelected)}
                        onClick={() => {
                            setPreset(key);
                            setCriteria(PRESETS[key]);
                        }}
                    >
                        {t(`heatmap.search.presets.${key}`)}
                    </button>
                ))}
            </div>

            {preset === null ? (
                <p className={sectionHint}>{t('heatmap.search.pickPreset')}</p>
            ) : (
            <>
            <div className="grid grid-cols-1 gap-3">
                {shows('reserve') && field('minReserveM2', t('heatmap.search.minReserve'), 'm² GF')}
                {shows('area') && (
                    <div className="grid grid-cols-2 gap-2">
                        {field('minAreaM2', t('heatmap.search.minArea'), 'm²')}
                        {field('maxAreaM2', t('heatmap.search.maxArea'), 'm²')}
                    </div>
                )}
                {shows('storeys') && field('minAddStoreys', t('heatmap.search.minAddStoreys'), t('heatmap.search.storeys'))}
                {shows('pv') && field('minPvYieldKWh', t('heatmap.search.minPv'), 'kWh/a')}
                {shows('brw') && field('maxBrw', t('heatmap.search.maxBrw'), '€/m²')}
            </div>

            {shows('signals') && (
                <div className="flex flex-col gap-1.5">
                    <span className="text-xs text-muted-foreground">{t('heatmap.search.conversionSignal')}</span>
                    <div className="flex flex-wrap gap-1.5">
                        {CONVERSION_SIGNALS.map((signal) => (
                            <button
                                key={signal}
                                type="button"
                                className={cn(
                                    'rounded-md border px-2 py-0.5 text-xs transition-colors hover:bg-accent',
                                    criteria.conversionSignals?.includes(signal) && 'border-foreground bg-accent font-bold',
                                )}
                                onClick={() => set({ conversionSignals: toggle(criteria.conversionSignals, signal) })}
                            >
                                {t(`heatmap.search.conversionSignals.${signal}`)}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {shows('basis') && (
                <div className="flex flex-col gap-1.5">
                    <HeadingWithLegend
                        label={t('heatmap.search.basis')}
                        infoLabel={t('heatmap.search.legend')}
                        entries={BASES.map((basis) => [t(`heatmap.search.bases.${basis}`), t(`heatmap.search.basisHints.${basis}`)])}
                    />
                    <div className="flex flex-wrap gap-1.5">
                        {BASES.map((basis) => (
                            <button
                                key={basis}
                                type="button"
                                className={cn(
                                    'rounded-md border px-2 py-0.5 text-xs transition-colors hover:bg-accent',
                                    criteria.basis?.includes(basis) && 'border-foreground bg-accent font-bold',
                                )}
                                title={t(`heatmap.search.basisHints.${basis}`)}
                                onClick={() => set({ basis: toggle(criteria.basis, basis) })}
                            >
                                {t(`heatmap.search.bases.${basis}`)}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {shows('uses') && (
                <div className="flex flex-col gap-1.5">
                    <HeadingWithLegend
                        label={t('heatmap.search.uses')}
                        infoLabel={t('heatmap.search.legend')}
                        entries={USES.map((use) => [use, t(`heatmap.search.useNames.${use}`)])}
                    />
                    <div className="flex flex-wrap gap-1.5">
                        {USES.map((use) => (
                            <button
                                key={use}
                                type="button"
                                className={cn(
                                    'rounded-md border px-2 py-0.5 text-xs transition-colors hover:bg-accent',
                                    criteria.uses?.includes(use) && 'border-foreground bg-accent font-bold',
                                )}
                                title={t(`heatmap.search.useNames.${use}`)}
                                aria-label={`${use}: ${t(`heatmap.search.useNames.${use}`)}`}
                                onClick={() => set({ uses: toggle(criteria.uses, use) })}
                            >
                                {use}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex flex-col gap-1">
                <Label htmlFor="search-sort" className="text-xs text-muted-foreground">
                    {t('heatmap.search.sort')}
                </Label>
                <select
                    id="search-sort"
                    className="h-8 rounded-md border bg-background px-2 text-sm"
                    value={criteria.sort ?? 'reserve'}
                    onChange={(event) => set({ sort: event.target.value as SearchSort })}
                >
                    {(['reserve', 'area', 'storeys', 'pv', 'brwAsc'] as SearchSort[]).map((sort) => (
                        <option key={sort} value={sort}>
                            {t(`heatmap.search.sorts.${sort}`)}
                        </option>
                    ))}
                </select>
            </div>

            <Button type="button" disabled={!hasArea || searching} onClick={() => onSearch(criteria)}>
                <Search className="mr-2 h-4 w-4" aria-hidden />
                {t('heatmap.search.run')}
            </Button>
            </>
            )}
            {!hasArea && <p className={sectionHint}>{t('heatmap.search.pickArea')}</p>}
        </div>
    );
}
